import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDealers } from "@insite/client-framework/Services/DealerService";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import customLoadDealers from "@ucs/Store/Custom/CustomLoadDealers";
import { getDealersDropdown, getGeoPermission } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import merge from "lodash/merge";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    content = "content",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
    };
    extendedStyles?: LocationDropdownStyles;
}
const mapStateToProps = (state: CustomState) => ({
    isGeoPermissionDenied: getGeoPermission(state),
    selectedLocationInContactForm: state.components.contactUsForm.fieldValues["selectedLocation"],
    dealersDropdown: getDealersDropdown(state),
});
const mapDispatchToProps = {
    setFieldValue,
    customLoadDealers,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;


export interface LocationDropdownStyles {
    formGroup?: InjectableCss;
    locationSelect?: SelectPresentationProps;
}

export const locationDropdownStyles: LocationDropdownStyles = {
    formGroup: {
        css: css`
            width: 100%;
            display: grid;
            gap: 20px;
            margin-top: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
};

const LocationDropdown = ({ extendedStyles, setFieldValue, customLoadDealers,selectedLocationInContactForm ,isGeoPermissionDenied, dealersDropdown }: Props) => {
    const [styles] = useState(() => merge(locationDropdownStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const contactFormContext = React.useContext(ContactFormContext);
    const [checkValidation, setCheckValidation] = useState(false);



    useEffect(() => {
           contactFormContext.validators.selectedLocationInContactForm = validateLocation;
           if (typeof selectedLocationInContactForm !== "undefined" && checkValidation ) {
                validateLocation();
           }

           customLoadDealers({} as any);
    }, [selectedLocationInContactForm]);

    useEffect(() => {
        const locationDropdownData = dealersDropdown.dealers || [];
        const locationOptions = locationDropdownData.map((item: DealerModel) => ({
            optionValue: `${item.name}, ${item.state}`,
            optionText: `${item.name}, ${item.state}`,
        }));
        setOptions(locationOptions);
        setCheckValidation(false);
        contactFormContext.validators.selectedLocationInContactForm = validateLocation;
        
    }, [isGeoPermissionDenied, dealersDropdown]);

    useEffect(() => {
        const updateSelectedLocationFromCookie = () => {
            if (isGeoPermissionDenied === "No") {
                setFieldValue({ key: "selectedLocation", value: getCookie("SelectedLocation") || "" });
            } else {
                setFieldValue({ key: "selectedLocation", value: "" });
            }
        };
        
        updateSelectedLocationFromCookie();

        const onStorage = (event: StorageEvent) => {
            if (event.key === "SelectedLocation") {
                updateSelectedLocationFromCookie();
            }
        };
        window.addEventListener("storage", onStorage);

        // Poll for same-tab cookie changes
        let lastCookie = getCookie("SelectedLocation");
        const interval = setInterval(() => {
            const currentCookie = getCookie("SelectedLocation");
            if (currentCookie !== lastCookie) {
                lastCookie = currentCookie;
                updateSelectedLocationFromCookie();
            }
        }, 1000);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(interval);
        };
    }, [isGeoPermissionDenied, setFieldValue]);
  
    const validateLocation = () => {
        const errorMessage = !selectedLocationInContactForm ? translate("Please make a selection.") : "";
        setSelectListError(errorMessage);
        return !errorMessage;
    };

    const listChangeHandler = (value?: string) => {    
        setCheckValidation(true);            
        setFieldValue({ key: "selectedLocation", value: value || ""});
    };     

    return (
        <StyledWrapper {...styles.formGroup}>
            <Select
                id="location-select"
                {...styles.locationSelect}
                label={translate("Nearest Location")}
                required={true}
                value={selectedLocationInContactForm || ""}
                error={selectListError}
                onChange={event => listChangeHandler(event.target.value)}
            >
                <option value="">{translate("Select Nearest Location")}</option>
                {options.map(option => (
                    <option key={option.optionValue} value={option.optionValue}>
                        {option.optionText}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LocationDropdown),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.content,
                displayName: "Location Dropdown",
                tooltip: "",
                editorTemplate: "DropDownField",
                options: [],
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
